import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { ContextGuide } from '../models/context-guide.model';
import { CONTEXT_GUIDES_REGISTRY } from '../constants/context-guides.registry';

@Injectable({ providedIn: 'root' })
export class ContextualHelpService {
  private readonly router = inject(Router);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly activeGuide = computed<ContextGuide>(() => {
    const url = this.currentUrl();
    const wildcard = this.wildcardRoute(url);
    const normalized = this.normalizeRoute(url);
    return this.resolveGuide(wildcard, normalized);
  });

  readonly isOpen = signal<boolean>(false);

  toggle(): void {
    this.isOpen.update(open => !open);
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  private resolveGuide(wildcard: string, normalized: string): ContextGuide {
    // Patrón con ":id" explícito: distingue rutas como "/branches/:id" (edición)
    // de su listado padre "/branches", que de otro modo colapsarían al mismo valor
    // una vez que se elimina el segmento dinámico.
    const wildcardMatch = CONTEXT_GUIDES_REGISTRY[wildcard];
    if (wildcardMatch) return wildcardMatch;

    const exact = CONTEXT_GUIDES_REGISTRY[normalized];
    if (exact) return exact;

    const segments = normalized.split('/').filter(Boolean);
    for (let i = segments.length - 1; i > 0; i--) {
      const prefix = '/' + segments.slice(0, i).join('/');
      const match = CONTEXT_GUIDES_REGISTRY[prefix];
      if (match) return match;
    }

    return CONTEXT_GUIDES_REGISTRY['default'];
  }

  private stripQueryAndHash(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  private normalizeRoute(url: string): string {
    const segments = this.stripQueryAndHash(url).split('/').filter(Boolean);
    const normalizedSegments = segments.filter(segment => !this.isDynamicSegment(segment));
    return '/' + normalizedSegments.join('/');
  }

  private wildcardRoute(url: string): string {
    const segments = this.stripQueryAndHash(url).split('/').filter(Boolean);
    const patterned = segments.map(segment => this.isDynamicSegment(segment) ? ':id' : segment);
    return '/' + patterned.join('/');
  }

  private isDynamicSegment(segment: string): boolean {
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
    const isNumeric = /^\d+$/.test(segment);
    return isUuid || isNumeric;
  }
}
